import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import LegalDocument from '../Legal/LegalDocument';
import {
  PRIVACY_SECTIONS,
  PRIVACY_EFFECTIVE,
  PRIVACY_UPDATED,
} from '../../assets/legal/legalContent';

interface CalendarConsentModalProps {
  visible: boolean;
  onDismiss: () => void;
  onConnect: () => Promise<void>;
  isConnecting?: boolean;
}

const CalendarConsentModal: React.FC<CalendarConsentModalProps> = ({
  visible,
  onDismiss,
  onConnect,
  isConnecting = false,
}) => {
  const [legalView, setLegalView] = useState<'privacy' | null>(null);

  useEffect(() => {
    if (!visible) setLegalView(null);
  }, [visible]);

  const DISCLOSURES = [
    'We read events with locations to show them on the map',
    'We add Ceen events you create to your calendar',
    'We never access your contacts, email, or other calendars',
  ];

  const modal = (
    <AnimatePresence>
      {visible && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100000,
          }}
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onDismiss}
            style={{
              position: 'absolute',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.45)',
              zIndex: 1,
            }}
          />

          {/* Sheet */}
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Connect Google Calendar"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 2,
              borderRadius: '20px 20px 0 0',
              backgroundColor: 'var(--color-surface)',
              boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
              paddingBottom: 'env(safe-area-inset-bottom, 16px)',
            }}
          >
            {/* Drag handle */}
            <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 4px' }}>
              <div
                style={{
                  width: '36px',
                  height: '4px',
                  borderRadius: '2px',
                  backgroundColor: 'var(--color-border-strong)',
                }}
              />
            </div>

            {/* Content */}
            <div
              style={{
                padding: '12px 24px 28px',
                maxWidth: '480px',
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '20px',
              }}
            >
              {/* Title */}
              <h2
                style={{
                  margin: 0,
                  fontSize: '17px',
                  fontWeight: 600,
                  color: 'var(--color-content)',
                  textAlign: 'center',
                }}
              >
                integrate your calendar
              </h2>

              {/* Disclosure bullets */}
              <ul
                style={{
                  margin: 0,
                  padding: 0,
                  listStyle: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                {DISCLOSURES.map(text => (
                  <li key={text} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    <span
                      style={{
                        flexShrink: 0,
                        width: '5px',
                        height: '5px',
                        borderRadius: '50%',
                        backgroundColor: 'var(--color-primary)',
                        marginTop: '8px',
                        opacity: 0.7,
                      }}
                    />
                    <span
                      style={{
                        fontSize: '14px',
                        lineHeight: 1.6,
                        color: 'var(--color-content-muted)',
                      }}
                    >
                      {text}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Privacy link */}
              <p
                style={{
                  margin: 0,
                  textAlign: 'center',
                  fontSize: '13px',
                  color: 'var(--color-content-muted)',
                }}
              >
                <button
                  onClick={() => setLegalView('privacy')}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    fontSize: '13px',
                    color: 'var(--color-primary)',
                    textDecoration: 'underline',
                    textUnderlineOffset: '2px',
                  }}
                >
                  See our Privacy Policy
                </button>
              </p>

              {/* Google-branded connect button — inline styles per Google brand guidelines */}
              <button
                onClick={onConnect}
                disabled={isConnecting}
                aria-busy={isConnecting}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  width: '100%',
                  padding: '10px 12px',
                  fontSize: '14px',
                  fontWeight: 500,
                  lineHeight: '20px',
                  whiteSpace: 'nowrap',
                  color: '#1F1F1F',
                  backgroundColor: '#F2F2F2',
                  border: 'none',
                  borderRadius: '20px',
                  cursor: isConnecting ? 'not-allowed' : 'pointer',
                  opacity: isConnecting ? 0.7 : 1,
                  transition: 'background-color 0.15s ease',
                }}
                onMouseEnter={e => {
                  if (!isConnecting)
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#E8E8E8';
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#F2F2F2';
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                  style={{ flexShrink: 0 }}
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                {isConnecting ? 'Connecting…' : 'Connect Google Calendar'}
              </button>

              {/* Dismiss link */}
              <button
                onClick={onDismiss}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontSize: '13px',
                  color: 'var(--color-content-muted)',
                  textAlign: 'center',
                  width: '100%',
                }}
              >
                Not now
              </button>
            </div>
          </motion.div>

          {/* Privacy policy overlay — position:absolute,inset:0 fills the fixed wrapper */}
          <LegalDocument
            visible={legalView === 'privacy'}
            title="Privacy Policy"
            subtitle="Privacy Policy"
            effectiveDate={PRIVACY_EFFECTIVE}
            updatedDate={PRIVACY_UPDATED}
            sections={PRIVACY_SECTIONS}
            onClose={() => setLegalView(null)}
          />
        </div>
      )}
    </AnimatePresence>
  );

  return createPortal(modal, document.body);
};

export default CalendarConsentModal;
